import { Box, Button, Icon, Tooltip, useDisclosure, useToast } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { plural, t } from '@lingui/core/macro';
import { Link as RouterLink } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { TranslationsToolbarPillContent } from './CourseTranslationsToolbarPill';
import { RetranslateScopeDialog } from './RetranslateDialogs';
import { useUnitTranslations } from './useUnitTranslations';
import type { TranslationsToolbarSummary } from './translationsSummary';
import { testIds } from './testIds';

// The unit-level equivalent of CourseTranslationsToolbarPill. Renders nothing
// until the flag is on and the unit has a published resource. A unit has no
// publish event of its own to kick off translations, so the popover carries the
// Translate action itself (a backfill over the unit's published resources).
export function UnitTranslationsToolbarPill({ unitId }: { unitId: string }) {
  const summary = useUnitTranslations(unitId);

  if (!summary.isActive) {
    return null;
  }

  return <UnitTranslationsToolbarPillContent unitId={unitId} summary={summary} />;
}

function UnitTranslationsToolbarPillContent({
  unitId,
  summary,
}: {
  unitId: string;
  summary: TranslationsToolbarSummary;
}) {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();

  // A run is already in flight, so re-triggering would be a no-op (the fan-out is
  // idempotent); disable rather than let it look like nothing happened.
  const running = summary.counts.inProgress > 0;
  // Everything already generated: Translate would only toast "nothing to do",
  // so the action becomes a forced re-run through the current engine instead
  // (demoted from primary, since it costs money without adding coverage).
  const fullyTranslated = summary.counts.total > 0 && summary.counts.ready === summary.counts.total;

  const confirmRetranslate = useDisclosure();
  const estimate = trpcAdmin.contentTranslations.retranslateUnitEstimate.useQuery(
    { unitId },
    { enabled: confirmRetranslate.isOpen },
  );
  const retranslate = trpcAdmin.contentTranslations.retranslateUnit.useMutation({
    onSuccess: ({ translationKickoff }) => {
      confirmRetranslate.onClose();
      const enqueued = translationKickoff.translationsEnqueued;
      toast({
        title: enqueued > 0 ? t`Re-translation started.` : t`Nothing to re-translate.`,
        description:
          enqueued > 0
            ? plural(enqueued, {
                one: 'Re-translating # translation.',
                other: 'Re-translating # translations.',
              })
            : undefined,
        status: enqueued > 0 ? 'success' : 'info',
        isClosable: true,
      });
      void utils.contentTranslations.listByUnit.invalidate({ unitId });
    },
    onError: () => {
      toast({ title: t`Couldn't start re-translation.`, status: 'error', isClosable: true });
    },
  });

  const trigger = trpcAdmin.contentTranslations.triggerUnitTranslations.useMutation({
    onSuccess: ({ translationKickoff }) => {
      const enqueued = translationKickoff.translationsEnqueued;
      toast({
        title: enqueued > 0 ? t`Translation started.` : t`Nothing to translate.`,
        description:
          enqueued > 0
            ? plural(enqueued, {
                one: 'Translating # translation.',
                other: 'Translating # translations.',
              })
            : t`Every published resource is already up to date, or none of this unit's courses have requested a language yet.`,
        status: enqueued > 0 ? 'success' : 'info',
        isClosable: true,
      });
      void utils.contentTranslations.listByUnit.invalidate({ unitId });
    },
    onError: () => {
      toast({ title: t`Couldn't start translation.`, status: 'error', isClosable: true });
    },
  });

  return (
    <TranslationsToolbarPillContent
      summary={summary}
      pillTestId={testIds.unitPill}
      popoverTestId={testIds.unitPopover}
      // Measured against every translatable language, so publishing won't fill
      // them all; the publish hint would mislead. The header Translate is the way
      // to generate, so no empty-state line is needed.
      emptyDetail={null}
      headerAction={
        fullyTranslated ? (
          <>
            <Tooltip label={t`Re-run every translation through the current engine`} openDelay={200}>
              <Button
                size="sm"
                variant="outline"
                onClick={confirmRetranslate.onOpen}
                data-testid={testIds.unitPopoverTranslate}
              >
                <Trans>Re-translate unit</Trans>
              </Button>
            </Tooltip>
            <RetranslateScopeDialog
              isOpen={confirmRetranslate.isOpen}
              onClose={confirmRetranslate.onClose}
              isLoading={retranslate.isPending}
              title={t`Re-translate this unit's content?`}
              estimate={estimate.data}
              onConfirm={() => retranslate.mutate({ unitId })}
            />
          </>
        ) : (
          <Tooltip
            label={
              running
                ? t`Translation in progress`
                : t`Only this unit's published resources are translated`
            }
            openDelay={200}
          >
            <Button
              size="sm"
              variant="primary"
              isLoading={trigger.isPending}
              isDisabled={running}
              onClick={() => trigger.mutate({ unitId })}
              data-testid={testIds.unitPopoverTranslate}
            >
              <Trans>Translate unit</Trans>
            </Button>
          </Tooltip>
        )
      }
      footer={
        <Box px={2} py={2} borderTopWidth="1px" borderColor="border.default">
          <Button
            as={RouterLink}
            to={`/units/${unitId}/translations`}
            size="sm"
            variant="ghost"
            w="full"
            justifyContent="space-between"
            rightIcon={<Icon as={ChevronRight} boxSize={iconSize.sm} />}
          >
            <Trans>View unit coverage</Trans>
          </Button>
        </Box>
      }
    />
  );
}
